import { type ApiCompanySummary } from "../../../models/api/admin/company";
import { type ApiDepartmentSummary } from "../../../models/api/admin/department";
import useAuth from "../../../auth";
import { useFetchCompany } from "../../../api/admin/company";
import { useEffect } from "react";
import useLocalStorage from "../../../hooks/useLocalStorage";

export const useCompanyDepartmentSelector = () => {
    const {
        selectedCompany,
        setSelectedCompany,
        selectedDepartment,
        setSelectedDepartment,
        userDetails,
    } = useAuth();

    const { fetchCompany } = useFetchCompany();
    /** When a Client with no children is logged in, need to change how "resetting" works */
    const [clientHasNoChildren, setClientHasNoChildren] =
        useLocalStorage<boolean>("clientHasNoChildren", false);

    const handleDefaultSelectedClient = async (companyId?: string) => {
        if (!companyId) {
            setSelectedCompany(null);
            return;
        }

        const response = await fetchCompany(companyId);

        if (response.success) {
            if (response.content.childCompanies.length === 0) {
                setSelectedCompany({
                    companyId,
                    companyName: response.content.companyName,
                    resellerId: response.content.resellerId,
                });
                setClientHasNoChildren(true);
            } else {
                setClientHasNoChildren(false);
            }
        }
    };

    useEffect(() => {
        if (!selectedCompany && !!userDetails?.companyId) {
            handleDefaultSelectedClient(userDetails.companyId);
        }
        if (!userDetails?.companyId) {
            setClientHasNoChildren(false);
        }
    }, [selectedCompany]);

    const handleDepartmentChange = (
        department: ApiDepartmentSummary | null,
        callback?: () => void
    ) => {
        setSelectedDepartment(department);
        callback && callback();
    };

    const handleCompanyChange = (
        company: ApiCompanySummary | null,
        callback?: () => void
    ) => {
        setSelectedCompany(company);
        handleDepartmentChange(null, callback);
    };

    const handleResetSelectedCompanyDepartment = () => {
        if (clientHasNoChildren) {
            handleDepartmentChange(null);
            return;
        }
        handleCompanyChange(null);
    };

    return {
        selectedDepartment,
        handleDepartmentChange,
        selectedCompany,
        handleCompanyChange,
        handleResetSelectedCompanyDepartment,
        clientHasNoChildren,
    };
};
